import PropTypes from "prop-types";
import React, { Component, ReactNode } from "react";
import { MSpinner, Map, StandardPinSize, MToolTip } from "components/Shared";
import { DataGrid as ReactDataGrid, SelectColumn, type Column, type RenderCellProps, type RenderHeaderCellProps } from "react-data-grid";
import "react-data-grid/lib/styles.css";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import Checkbox from "@mui/material/Checkbox";
import MultiSelectFilter from "components/Search/MultiSelectFilter";
import SearchTextFilter from "components/Search/SearchTextFilter";
import xlsx from "xlsx";
import moment from "moment-timezone";
import { savePreference, loadPreference, preferenceJustLoaded } from "actions/preferences";
import { debounce } from "lodash";
import { connect } from "react-redux";
import {
  booleanToYesNo,
  dateTimeToStringWithNumOfDays,
  moneyFormatter,
  dateToStringYy,
  dateTimeToString,
  dateToString,
  commaFormat,
  dateTimeToStringWithDOW,
  dateToStringWithDOW,
  formatDecimalPercent2,
  formatPercentWholeNumber,
  formatPercent,
} from "components/Shared/Formatters";
import { normalizeNumberVariableDecimal, formatNumberConsistentDecimalLength } from "components/FormElements";
import {
  DATA_TYPE_NUMBER,
  DATA_TYPE_NUMBER_HUNDREDTHS,
  DATA_TYPE_NUMBER_TENTHS,
  DATA_TYPE_PERCENT,
  DATA_TYPE_NUMBER_RAW,
  DATA_TYPE_MONEY,
  DATA_TYPE_BOOLEAN,
  DATA_TYPE_DATE,
  DATA_TYPE_DATE_TIME,
  DATA_TYPE_DAYS_ELAPSED,
  DATA_TYPE_DATETIME_WITHDOW,
  DATA_TYPE_DATE_WITHDOW,
  DATA_TYPE_STRING,
  DATA_TYPE_BUTTON,
  DATA_TYPE_TEXT_BUTTON,
  DATA_TYPE_NUMBER_UNFORMATTED,
  DATA_TYPE_PERCENT_WHOLE_NUMBER,
  DATA_TYPE_PERCENT_DECIMAL,
} from "typeConstants/dataTypes";
import { formatWorksheetColumns, isNumeric } from "components/Shared/Functions";
import { ACTION_CLONE } from "./MDialog";

// Custom implementation to replace react-data-grid-addons Selectors
// This filters rows based on the provided filters
const filterRows = (rows: any[], filters: Record<string, any>) => {
  if (!filters || Object.keys(filters).length === 0) {
    return rows;
  }
  
  return rows.filter(row => {
    return Object.keys(filters).every(columnKey => {
      const filter = filters[columnKey];
      if (!filter || !filter.filterTerm) return true;
      
      const cellValue = row[columnKey];
      
      // Handle multi-select filters (array of values)
      if (Array.isArray(filter.filterTerm)) {
        if (filter.filterTerm.length === 0) return true;
        const filterValues = filter.filterTerm.map(f => f.value || f);
        return filterValues.includes(cellValue);
      }
      
      // Handle custom filterValues function
      if (filter.filterValues) {
        return filter.filterValues(row, filter, columnKey);
      }
      
      // Handle text filter
      if (cellValue == null) return false;
      return String(cellValue).toLowerCase().includes(String(filter.filterTerm).toLowerCase());
    });
  });
};

// Simple wrapper component to replace DraggableContainer
// Column reordering will be handled via the grid's native draggable column headers
const DraggableContainer = ({ children, onHeaderDrop }: { children: React.ReactNode, onHeaderDrop?: (source: string, target: string) => void }) => {
  return <>{children}</>;
};

export interface MGridColumnDefinition<T = any> {
  key?: string, // every column needs a unique key for saving column configurations/a lot of other things. this value will not display anywhere
  code?: string, // column code to pull defaults from
  name?: string, // the column name to show in the grid header
  dataSource: (keyof T) | ((row: T) => string) | ((row: T) => React.JSX.Element), // provides data for the grid (either a string of a property name to pull off of the dataSource prop or a function that will be invoked with the entire row passed in from dataSource)
  headerTooltip?: string, // optional - overrides the regular tooltip on the header that defaults to what the header text is
  tooltip?: string | ((row: any) => string) | null, // optional - provides a custom tooltip for the cell. Defaults to cell value. Set to null to disable tooltip.
  sortValue?: (keyof T) | ((row: T) => string), // optional - overrides the sort logic and uses this value to sort on instead (either a string of a property name to pull off of the dataSource prop or a function that will be invoked with the entire row passed in from dataSource)
  hideFromExport?: boolean // optional - default false, if set to true this column will not show up in the grid export
  exportValue?: string, // optional - overrides the export logic and exports this value instead (either a string of a property name to pull off of the dataSource prop or a function that will be invoked with the entire row passed in from dataSource)
  width?: number, // pixel value of column width, if not provided react data grid will try to make the widths work out to the width of the grid
  visible?: boolean, // decides if the column is shown by default. if false the column can be shown if configuring of the grid is allowed
  resizable?: boolean, // decides if the column is resizable at all. this is currently pretty broken and does not save with the configuration
  sortable?: boolean, // decides if the column is sortable
  dataType?: string, // defines the data type for the column which handles how we sort and display the value, look at the DATA_TYPE_... constants for options in this file
  filterType?: string, // decides what kind of filter to show on the filter bar for the column, look at the FILTER_TYPE_... constants for options in this file
  filterRenderer?: ReactNode | (() => ReactNode), // a function or fragment to show in the filter bar
  formatter?: (params: { value: ReactNode, row: DataRow }) => ReactNode, // currently either FORMATTER_CENTER_ALIGN | FORMATTER_RIGHT_ALIGN. does not play well with tooltip override
  valueFormatter?: (value: any) => (ReactNode | string),

  order?: number, // dont specify this, its just needed for logic below
  headerRenderer?: ReactNode,
};

export interface MGridAction {
  code?: string,
  icon?: string, // react md icon string or something like that
  buttonText?: string,
  helpText?: string,
  onClick?: () => void,
  order?: number,
  disabled?: boolean,
};

export interface MGridFilter {
  filterTerm?: { value: string, label: string }[],
  rawValue?: { value: string, label: string }[],
  mapSelected?: { value: string, label: string }[],
  filterValues?: (row: any, columnFilter: any, columnKey: any) => any, // type of const  filterValues = function (row, columnFilter, columnKey) {
};

export interface MGridColumnConfiguration {
  key: string,
  order: number,
  width: number,
  visible: boolean,
  filter?: string[],
};

export interface MapPin {
  id: number,
  latitude: string,
  longitude: string,
  icon: { url: string, width: number, height: number },
};

type DataRow = Record<string, any>;

interface Props {
  className?: string,
  wrapperStyle?: React.CSSProperties, //custom styles for the grid wrapper div (e.g. height, maxHeight, overflow)
  configurationId?: string, //used for saving users configuration of the grid
  headerText?: string, //defines the text to put in the grid header. is also used to name the export file if the download functionality is enabled and downloadFileName is not provided
  downloadFileName?: string, //file name for the download - {date} will get replaced with the date in the same format that the filename usually gets a date (.xlsx extension gets added)
  columnDefinitions: MGridColumnDefinition[],
  data: Record<string, any>[], //an array of data to show in the grid. the way it shows up is defined by the column definitions
  dataLastChanged?: number | string | Date, //pass in a new timestamp to tell the grid its data changed if the old data and new data have the same amount of rows (we dont "deep compare" the rows to check for equality, we only check the count)
  isFetchingData?: boolean, //if set to true the grid will show a loading spinner
  actions?: (MGridAction | string)[],
  draggableColumns?: boolean, //if true the columns will be reorderable
  allowRowSelection?: boolean, //if true the grid will show a new checkbox column that will allow us to select rows
  onRowSelectionChanged?: (selectedOriginalIndexes: number[]) => void, //gets called with the original index (not counting sorting or filtering) of the grid rows that have been selected, row selection with the grid works like a controlled component, so you must pass these values back in to the grid prop selectedRows
  selectedRows?: number[], //pass in the rows that are currently selected (that come from onRowSelectionChanged) if using allowRowSelection
  onRowClick?: (selectedOriginalIndex: number) => void, //function that will be invoked on row click, original row index (not counting sorting or filtering) will be passed in
  onCellButtonClick?: (selectedOriginalIndex: number, columnKey: string) => void, //function that will be invoked with original row index, column key when a cell with DATA_TYPE_BUTTON or DATA_TYPE_TEXT_BUTTON is clicked
  preferences?: object, //this is provided from redux, do not provide it yourself, it is used to save and load grid configurations
  loadPreference?: (preferenceName: string) => void, //this is provided from redux, do not provide it yourself, it is used to save and load grid configurations
  savePreference?: (preferenceName: string, config: MGridColumnConfiguration[]) => void, //this is provided from redux, do not provide it yourself, it is used to save and load grid configurations
  /*  this will be called for every row in the filtered grid dataset
      the object is passed into this callback in the same exact form as it was passed in
      return the following object
      { latitude, longitude, iconURL } */
  getMapPinForRow?: (originalIndex: number) => { latitude: string, longitude: string, iconURL: string },
  /*  this will be called for a row when the mouse hovers over it
      the object is passed into this callback in the same exact form as it was passed in
      return
      {
          title: string
          children: JSX
      } */
  getMapToolTipForRow?: (originalIndex: number) => { title: string, children: ReactNode },
  displayNumOfItemsFound?: boolean, //if true the x Items Found text will display in the header
  disableFilters?: boolean, //set this to true to disable filters across the board so you dont have to set them to false with every column
  rowLastClicked?: number,
  /*
    callback that will expose some methods on the grid to be called from parents components
    {
      downloadGrid, causes a download of the grid
    }
  */
  getControlMethods?: (methods: { downloadGrid: () => void}) => void,
  cardStyle?: boolean, // Styles MGrid header like MCard header, see .card-style sccs class
};

interface State {
  viewMode: string,
  columnDefinitions: MGridColumnDefinition[],
  dataSource: DataRow[],
  pinsForMap: MapPin[],
  mouseOverControl: ReturnType<Props["getMapToolTipForRow"]>,
  sortColumn: string,
  sortDirection: "NONE" | "ASC" | "DESC",
  columnConfigForDialog: {
    key: string,
    visible: boolean,
    order: number,
    name: string,
  }[],
  dropdownFilterValues: Record<string, any>,
  showFilterBar: boolean,
  shiftKeyDown: boolean,
  lastIndexSelected: number,
  singleRowSelectedIndex: number,
  showConfigureDialog: boolean,
};

class MGrid extends Component<Props, State> {
  rateLimitedSaveColumnConfig: () => void;
  rateLimitedResize: () => void;
  columnConfig: MGridColumnConfiguration[];

  static defaultProps = {
    displayNumOfItemsFound: true,
    visible: true,
  };

  constructor(props: Props) {
    super(props);

    this.updateColumnDefinitions = this.updateColumnDefinitions.bind(this);
    this.getTransformedColumnDefinitions = this.getTransformedColumnDefinitions.bind(this);
    this.defaultColumnConfig = this.defaultColumnConfig.bind(this);
    this.updateDataSource = this.updateDataSource.bind(this);
    this.rowGetter = this.rowGetter.bind(this);
    this.onHeaderDrop = this.onHeaderDrop.bind(this);
    this.onToggleMapView = this.onToggleMapView.bind(this);
    this.onDownloadGrid = this.onDownloadGrid.bind(this);
    this.onConfigureColumns = this.onConfigureColumns.bind(this);
    this.onConfigureColumnsReset = this.onConfigureColumnsReset.bind(this);
    this.onConfigureColumnsCancelled = this.onConfigureColumnsCancelled.bind(this);
    this.onConfigureColumnsApplied = this.onConfigureColumnsApplied.bind(this);
    this.onGridSort = this.onGridSort.bind(this);
    this.sortData = this.sortData.bind(this);
    this.comparer = this.comparer.bind(this);
    this.getAction = this.getAction.bind(this);
    this.getGridActions = this.getGridActions.bind(this);
    this.toggleColumnVisible = this.toggleColumnVisible.bind(this);
    this.handleFilterChange = this.handleFilterChange.bind(this);
    this.getValidFilterValues = this.getValidFilterValues.bind(this);
    this.preferenceName = this.preferenceName.bind(this);
    this.saveColumnConfig = this.saveColumnConfig.bind(this);
    this.onRowClick = this.onRowClick.bind(this);
    this.onMapClick = this.onMapClick.bind(this);
    this.getFilters = this.getFilters.bind(this);
    this.onRowsSelected = this.onRowsSelected.bind(this);
    this.onRowsDeselected = this.onRowsDeselected.bind(this);
    this.getSelectedIndexes = this.getSelectedIndexes.bind(this);
    this.getSelectedRowKeys = this.getSelectedRowKeys.bind(this);
    this.getGridColumns = this.getGridColumns.bind(this);
    this.keyHandler = this.keyHandler.bind(this);
    this.getNumItemsFoundText = this.getNumItemsFoundText.bind(this);
    this.onMouseOver = this.onMouseOver.bind(this);
    this.onMouseOut = this.onMouseOut.bind(this);
    this.deepCompareArray = this.deepCompareArray.bind(this);
    this.rateLimitedSaveColumnConfig = debounce(this.saveColumnConfig, 1000);
    this.onClearFilters = this.onClearFilters.bind(this);
    this.gridWrapperCallbackRef = this.gridWrapperCallbackRef.bind(this);
    this.rateLimitedResize = debounce(() => window.dispatchEvent(new Event("resize")), 100);

    //this does not need to be in state because it doesnt directly effect the render
    //also if it is in state, it gets annoying because you need to clone it every time before changing it
    //please do not add anything unnecessary to this because it all gets saved to the database
    this.columnConfig = null; //[{key, order, width, visible, filter}]

    this.state = {
      viewMode: VIEW_MODE_GRID,
      columnDefinitions: null,
      dataSource: null,
      pinsForMap: null,
      mouseOverControl: null,
      sortColumn: null,
      sortDirection: null,
      columnConfigForDialog: null,
      dropdownFilterValues: {},
      showFilterBar: false,
      shiftKeyDown: false,
      lastIndexSelected: null,
      singleRowSelectedIndex: null, //if row selection is off, when we click a row we want it to highlight (so we will count it as selected)
      showConfigureDialog: false,
    };
  }

  componentDidMount() {
    this.defaultColumnConfig();
    this.updateColumnDefinitions();
    if (this.props.configurationId) this.props.loadPreference(this.preferenceName());

    document.addEventListener("keydown", this.keyHandler);
    document.addEventListener("keyup", this.keyHandler);

    if (this.props.getControlMethods) this.props.getControlMethods({ downloadGrid: this.onDownloadGrid });
  }

  //maybe switch this to a deriveStateFromProps (but that makes me store some stuff in props that i didnt want to) will save 1 render
  componentDidUpdate(prevProps) {
    if (prevProps.configurationId != this.props.configurationId) {
      this.defaultColumnConfig();
      this.setState({ viewMode: VIEW_MODE_GRID, sortColumn: null, sortDirection: null, lastIndexSelected: null });
      this.props.loadPreference(this.preferenceName());
    }

    if (prevProps.columnDefinitions && this.props.columnDefinitions) {
      if (!this.deepCompareArray(prevProps.columnDefinitions, this.props.columnDefinitions)) {
        this.defaultColumnConfig();
        this.setState({ viewMode: VIEW_MODE_GRID, sortColumn: null, sortDirection: null, lastIndexSelected: null });
        this.updateColumnDefinitions();
      } else if (this.props.dataLastChanged !== prevProps.dataLastChanged && this.props.columnDefinitions.find(d => !!d.filterRenderer)) {
        this.updateColumnDefinitions();
      }
    }
    else {
      this.updateColumnDefinitions();
    }

    if ((prevProps.data || []).length != (this.props.data || []).length || (prevProps.isFetchingData && !this.props.isFetchingData) || prevProps.dataLastChanged != this.props.dataLastChanged) {
      this.updateDataSource(true);
    }

    if (preferenceJustLoaded(this.preferenceName(), prevProps.preferences, this.props.preferences)) {
      let config = this.props.preferences[this.preferenceName()].value;
      if (config && config.length > 0) {
        //custom column config was loaded, we are going to merge it with the default config in case the config is old and columns have been added or removed
        this.defaultColumnConfig();

        this.columnConfig.forEach((cc, i) => {
          let newcc = config.find(ncc => cc.key === ncc.key);
          if (newcc) {
            this.columnConfig[i] = { ...newcc };//need to copy it to avoid state mutation later on
            delete this.columnConfig[i].filter; //dont want to save or load filters anymore SD-11115
          } else {
            //new columns will be hidden by default
            cc.visible = false;
          }
        });

        this.updateColumnDefinitions();
      }
    }
    //if someone changes allowRowSelection lets unload and reload the grid because the react data grid wont update if we just change those props
    if (prevProps.allowRowSelection != this.props.allowRowSelection) {
      this.updateDataSource(true);
    }
    if (this.props.rowLastClicked !== this.state.singleRowSelectedIndex) {
      this.setState({ singleRowSelectedIndex: this.props.rowLastClicked });
    }
  }

  componentWillUnmount() {
    document.removeEventListener("keydown", this.keyHandler);
    document.removeEventListener("keyup", this.keyHandler);
  }

  //before you ask, yes i tried using lodash isEqual, but we need to not compare the functions (formatters are functions)
  //then i tried return isEqual(omit(a1, functions(a1)), omit(a2, functions(a2)));
  //that should do a deep compare without worrying about functions, but it still didnt work
  //so then i wrote my own just for col defs
  deepCompareArray(a1, a2) {
    if ((a1 || []).length != (a2 || []).length) return false;

    const primitiveKeys = (o) => Object.keys(o).filter(k => ["boolean", "number", "string"].some(t => typeof (o[k]) === t));

    return a1 && !a1.some((_, i) => {
      let k1 = primitiveKeys(a1[i]);
      let k2 = primitiveKeys(a2[i]);
      let allKeys = Array.from(new Set(k1.concat(k2)));
      if (k1.length !== k2.length || k1.length !== allKeys.length) return true;
      return allKeys.some(k => a1[i][k] !== a2[i][k]);
    });
  }

  keyHandler(e) {
    if (e.shiftKey != this.state.shiftKeyDown)
      this.setState({ shiftKeyDown: e.shiftKey });
  }

  onMouseOver(pin) {
    if (this.props.getMapToolTipForRow)
      this.setState({ mouseOverControl: this.props.getMapToolTipForRow(pin.id) });
  }

  onMouseOut() {
    this.setState({ mouseOverControl: null });
  }

  preferenceName() {
    return PREFERENCE_PREFIX + this.props.configurationId;
  }

  onToggleMapView() {
    if (this.state.viewMode === VIEW_MODE_GRID) this.setState({ viewMode: VIEW_MODE_MAP });
    else this.setState({ viewMode: VIEW_MODE_GRID });
  }

  onDownloadGrid() {
    const getExportValue = (row, cd) => {
      switch (cd.dataType) {
        case DATA_TYPE_DAYS_ELAPSED:
          return dateToString(row[cd.key + RAW_ORIGINAL]);
        case DATA_TYPE_MONEY:
        case DATA_TYPE_NUMBER_RAW:
        case DATA_TYPE_NUMBER_UNFORMATTED:
        case DATA_TYPE_NUMBER_HUNDREDTHS:
        case DATA_TYPE_NUMBER_TENTHS:
        case DATA_TYPE_NUMBER:
        case DATA_TYPE_PERCENT:
        case DATA_TYPE_PERCENT_WHOLE_NUMBER:
        case DATA_TYPE_PERCENT_DECIMAL:
          //to be formatted with excel formatters later
          return isNumeric(row[cd.key + RAW_ORIGINAL]) ? parseFloat(row[cd.key + RAW_ORIGINAL]) : row[cd.key + RAW_ORIGINAL];
        case DATA_TYPE_TEXT_BUTTON:
          // The value is nested inside a series of react components
          return row[cd.key].props?.children?.props?.children?.props?.children;
        default:
          return row[cd.key];
      }
    };

    let filteredColumnDefinitions = this.state.columnDefinitions.filter(cd => !cd.hideFromExport);

    let cols = filteredColumnDefinitions.map(cd => cd.name);
    let rows = this.state.dataSource.map(row => filteredColumnDefinitions.reduce((acc, cd) => {
      acc[cd.name] = row[cd.key + EXPORT_VALUE] === undefined ? getExportValue(row, cd) : row[cd.key + EXPORT_VALUE];
      return acc;
    }, {}));

    let wb = xlsx.utils.book_new();
    //provide an explicit column order because it just uses the order from object.keys which isnt quite garunteed to be consistent
    let ws = xlsx.utils.json_to_sheet(rows, { header: cols });

    formatWorksheetColumns(filteredColumnDefinitions, ws);

    let dateString = moment().format("YYYY.MM.DD-HH.mm.ss");
    xlsx.utils.book_append_sheet(wb, ws, "Sheet 1");
    let filename = this.props.downloadFileName || `${this.props.headerText}-${dateString}`;
    // @ts-ignore i promise replaceAll exists
    filename = filename.replaceAll("{date}", dateString);
    xlsx.writeFile(wb, `${filename}.xlsx`);
  }

  onConfigureColumns() {
    let tcd = this.getTransformedColumnDefinitions();

    let config = this.columnConfig.map(cc => ({
      key: cc.key,
      visible: cc.visible,
      order: cc.order,
      name: tcd.find(cd => cd.key === cc.key).name
    }));

    config.sort(colConfigSort);

    this.setState({ showConfigureDialog: true, columnConfigForDialog: config });
  }

  onConfigureColumnsCancelled() {
    this.setState({ columnConfigForDialog: null });
  }

  onConfigureColumnsReset() {
    this.onConfigureColumnsCancelled();
    this.defaultColumnConfig();
    this.updateColumnDefinitions();
    this.saveColumnConfig();
  }

  onConfigureColumnsApplied() {
    if (!this.state.columnConfigForDialog.some(cc => cc.visible)) {
      alert("Please select at least one column to view");
      return;
    }

    let i = 0;
    this.state.columnConfigForDialog.forEach(ccd => {
      let col = this.columnConfig.find(cc => cc.key === ccd.key);
      col.visible = ccd.visible;
      if (!col.visible) delete col.filter;
      if (ccd.visible) {
        col.order = i;
        i++;
      } else {
        col.order = -1;
      }
    });

    this.saveColumnConfig();
    this.setState({ columnConfigForDialog: null });
    this.updateColumnDefinitions();
  }

  onClearFilters() {
    for (let i = 0; i < 10; i++)setTimeout(() => window.dispatchEvent(new Event("resize")), i * 100);
    this.columnConfig.forEach(cc => cc.filter = null);
    this.updateColumnDefinitions();
  }

  getAction(a) {
    let action = a;

    if (typeof a === "string") action = { code: a };

    let actionDefaults;
    switch (action.code) {
      case ACTION_MAP:
        actionDefaults = {
          icon: this.state.viewMode === VIEW_MODE_GRID ? "pin_drop" : "grid_on",
          helpText: this.state.viewMode === VIEW_MODE_GRID ? "Map View" : "Grid View",
          order: 9600,
          onClick: this.onToggleMapView
        };
        break;
      case ACTION_DOWNLOAD:
        actionDefaults = {
          icon: "cloud_download",
          helpText: "Download",
          order: 9700,
          onClick: this.onDownloadGrid
        };
        break;
      case ACTION_REFRESH:
        actionDefaults = {
          icon: "refresh",
          helpText: "Refresh Grid",
          order: 9800,
        };
        break;
      case ACTION_CONFIGURE:
        actionDefaults = {
          icon: "view_week",
          helpText: SELECT_COLUMNS,
          order: 9900,
          onClick: this.onConfigureColumns
        };
        break;
      case ACTION_ADD:
        actionDefaults = {
          icon: "add",
          helpText: "Add Row",
          order: 9500
        };
        break;
      case ACTION_DELETE:
        actionDefaults = {
          icon: "remove",
          helpText: "Delete Row",
          order: 10100
        };
        break;
      case ACTION_EDIT:
        actionDefaults = {
          icon: "create",
          helpText: "Edit Row",
          order: 10200
        };
        break;
      case ACTION_CANCEL:
        actionDefaults = {
          icon: "clear",
          helpText: "Cancel",
          order: 10300
        };
        break;
      case ACTION_SAVE:
        actionDefaults = {
          icon: "save",
          helpText: "Save Data",
          order: 10400
        };
        break;
      case ACTION_CLONE:
        actionDefaults = {
          icon: "content_copy",
          helpText: "Save as New Item",
          order: 10500,
        };
        break;
      default:
        actionDefaults = {};
        break;
    }

    return { ...actionDefaults, ...action };
  }

  getGridActions() {
    let actions = this.props.actions ? this.props.actions.map(a => this.getAction(a)) : [];
    actions.sort((a, b) => a.order > b.order ? 1 : -1);
    return actions.map((a, i) => (
      <i key={`action_${i}`} className={`md-icon material-icons ${a.className || ""}`} title={a.helpText} onClick={a.onClick}>
        {a.icon}
      </i>));
  }

  defaultColumnConfig() {
    const transformedColumnDefinitions = this.getTransformedColumnDefinitions();
    this.columnConfig = transformedColumnDefinitions && transformedColumnDefinitions.map((cd, i) => ({
      key: cd.key,
      order: i,
      width: cd.width,
      visible: cd.visible,
    }));
  }

  saveColumnConfig() {
    if (this.props.configurationId && this.props.savePreference) {
      //need to copy columnConfig before saving because it gets put in state and then if we modify it we would be modifying state
      this.props.savePreference(this.preferenceName(), this.columnConfig.map(cc => {
        let o = { ...cc };
        delete o.filter;
        return o;
      }));
    }
  }

  getTransformedColumnDefinitions(): MGridColumnDefinition[] {
    let getFilterProps = (filterType, key, filterRenderer) => {
      if (typeof filterRenderer === "function") {
        return {
          filterable: true, filterRenderer: props => filterRenderer(props)
        };
      } else if (filterRenderer) {
        return {
          filterable: true, filterRenderer: () => filterRenderer
        };
      }
      switch (filterType) {
        case FILTER_TYPE_TEXT:
          return {
            filterable: true, filterRenderer: props => (
              <SearchTextFilter
                {...props}
                defaultValue={getDefaultValue(key)}
                isValidGrid={false}
              />)
          };
        case FILTER_TYPE_MULTI_SELECT:
          return {
            filterable: true, filterRenderer: props => (
              <MultiSelectFilter
                {...props}
                defaultValue={getDefaultValue(key)}
                filterValues={filterValues}
                isValidGrid={false}
              />)
          };
        case FILTER_TYPE_MULTI_SELECT_NOLABEL:
          return {
            filterable: true, filterRenderer: props => (
              <MultiSelectFilter
                {...props}
                hideLabel={true}
                defaultValue={getDefaultValue(key)}
                filterValues={filterValues}
                isValidGrid={false}
              />)
          };
        case FILTER_TYPE_NONE:
          return {};
      }
    };

    //using a function to get the default value instead of just setting it so when the grid gets remounted the controls have proper values
    //example: adding a filter to the grid, and then using the server side filter functionality was clearing the text from the filters because the fiters would get reloaded with their original defaults
    let getDefaultValue = (key) => {
      if (this.columnConfig) return this.columnConfig.find(cc => cc.key === key).filter;
      else return null;
    };

    const { columnDefinitions } = this.props;

    let cols = columnDefinitions && columnDefinitions.map(cd =>
    ({
      ...getGridColumnDefaultsFromCode(cd.code),
      ...cd,
      draggable: typeof this.props.draggableColumns === "undefined" ? true : this.props.draggableColumns //want to default this to true
    })
    ).map(c => //second map required because the filter type is getting figured out in the previous map and either comes from defaults or the cd variable, so we reference that then here once its figured out
    {
      return {
        ...c,
        ...getFilterProps(c.filterType, c.key, c.filterRenderer)
      };
    });

    // @ts-ignore
    return cols;
  }

  updateColumnDefinitions() {
    let coldefs = this.getTransformedColumnDefinitions();
    if (coldefs) {
      coldefs.forEach(cd => {
        let cc = this.columnConfig.find(cc => cc.key === cd.key);
        cd.order = cc.order;
        cd.visible = cc.visible;
        if (!cd.headerRenderer) cd.headerRenderer = <span title={cd.headerTooltip || cd.name}>{cd.name}</span>;
      });

      coldefs.sort((a, b) => a.order > b.order ? 1 : -1);
      coldefs.forEach(cd => delete cd.order);//im worried this could like mess with the react data grid, doubt it would but who knows

      coldefs = coldefs.filter(cd => cd.visible);

      if (this.props.configurationId) {
        let noId = coldefs.find(cd => !cd.key);
        if (noId) throw `If providing MGrid with a configurationId, each column needs its own unique key. Column "${noId.name}" does not have a key. Column ids are used for saving user configuration`;

        let idSet = new Set(coldefs.map(cd => cd.key));
        if (idSet.size != coldefs.length) throw `If providing MGrid with a configurationId, each column key needs to be unique. Column ids are used for saving user configuration`;
      }

      coldefs.forEach(cd => {
        // Show tooltip on all cells by default, unless tooltip is explicitly set to null
        // Only apply default formatter if no custom formatter exists
        if (cd.tooltip !== null && !cd.formatter) {
          cd.formatter = data => <div title={data.row[cd.key + TIP_TEXT]} style={{ width: "100%", height: "100%", display: "flex", alignItems: "center" }}>{data.row[cd.key]}</div>;
        }
      });

      let showFilterBar = !this.props.disableFilters && coldefs.some(cd => cd.filterType && cd.filterType != FILTER_TYPE_NONE);

      //first null out columnDefinitions, and then re set them. This causes the grid to re render and resolves react-data-grid issues
      //that happen when the columns are swapped out from under it (grid height messes up and rows blank out sometimes)
      //finally, update the data source
      this.setState({ columnDefinitions: null }, () => this.setState({ columnDefinitions: coldefs, showFilterBar: showFilterBar }, this.updateDataSource));
    }
  }

  getFilters() {
    if (!this.columnConfig) return null;

    let ret: Record<string, MGridFilter> = this.columnConfig.filter(cc => cc.filter).reduce((acc, cc) => {
      let filter: MGridFilter = {};
      if (typeof cc.filter === "string") {
        filter.filterTerm = cc.filter;
        filter.rawValue = cc.filter;
      } else {
        let filterValue = cc.filter.map(f => ({ value: f, label: f }));
        filter.filterTerm = filterValue;
        filter.rawValue = filterValue;
        filter.mapSelected = filterValue;
        filter.filterValues = filterValues;
      }

      acc[cc.key] = filter;
      return acc;
    }, {});

    return ret;
  }

  updateDataSource(sendNullFirst = false) {
    if (this.props.isFetchingData || !this.props.data || !this.state.columnDefinitions) {
      this.setState({ dataSource: null });
      return;
    }

    let getValue = (col, dr, field) => {
      switch (typeof col[field]) {
        case "function":
          return col[field](dr);
        case "string":
          return col[field].split(".").reduce((acc2, val) => acc2 ? acc2[val] : undefined, dr);
        default:
          return null;
      }
    };

    let newDS = this.props.data.map((dr, i) =>
      this.state.columnDefinitions.reduce((acc, col) => {
        acc[col.key] = getValue(col, dr, "dataSource");
        // If tooltip is explicitly null, no tooltip. If tooltip is defined, use it. Otherwise, default to cell value.
        if (col.tooltip !== null) {
          acc[col.key + TIP_TEXT] = col.tooltip !== undefined ? getValue(col, dr, "tooltip") : acc[col.key];
        }
        if (col.sortValue) acc[col.key + SORT_VALUE] = getValue(col, dr, "sortValue");
        if (col.exportValue) acc[col.key + EXPORT_VALUE] = getValue(col, dr, "exportValue");

        acc[col.key + RAW_ORIGINAL] = acc[col.key];

        let valueFormatter = col.valueFormatter;

        if (!valueFormatter) {
          switch (col.dataType) {
            case DATA_TYPE_NUMBER:
              valueFormatter = normalizeNumberVariableDecimal();
              break;
            case DATA_TYPE_NUMBER_HUNDREDTHS:
              valueFormatter = formatNumberConsistentDecimalLength();
              break;
            case DATA_TYPE_NUMBER_TENTHS:
              valueFormatter = formatNumberConsistentDecimalLength(1, false);
              break;
            case DATA_TYPE_PERCENT:
              valueFormatter = formatPercent;
              break;
            case DATA_TYPE_PERCENT_WHOLE_NUMBER:
              valueFormatter = formatPercentWholeNumber;
              break;
            case DATA_TYPE_PERCENT_DECIMAL:
              valueFormatter = formatDecimalPercent2(0, true);
              break;
            case DATA_TYPE_NUMBER_UNFORMATTED:
              valueFormatter = normalizeNumberVariableDecimal(0, true);
              break;
            case DATA_TYPE_MONEY:
              valueFormatter = moneyFormatter(2);
              break;
            case DATA_TYPE_BOOLEAN:
              valueFormatter = booleanToYesNo;
              break;
            case DATA_TYPE_DATE:
              valueFormatter = dateToStringYy;
              break;
            case DATA_TYPE_DATE_TIME:
              valueFormatter = dateTimeToString;
              break;
            case DATA_TYPE_DAYS_ELAPSED:
              valueFormatter = dateTimeToStringWithNumOfDays;
              break;
            case DATA_TYPE_DATETIME_WITHDOW:
              valueFormatter = dateTimeToStringWithDOW;
              break;
            case DATA_TYPE_DATE_WITHDOW:
              valueFormatter = dateToStringWithDOW;
              break;
            case DATA_TYPE_BUTTON:
              valueFormatter = (icon) => icon && (
                <div style={{ textAlign: "center" }}>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      if (this.props.onCellButtonClick) {
                        // make the row stay highlighted
                        let originalIndex = this.state.dataSource.find(src => src.originalIndex === i)[ORIGINAL_INDEX];
                        this.setState({ singleRowSelectedIndex: originalIndex });
                        this.props.onCellButtonClick(i, col.key);
                      }
                    }}
                  >
                    <Icon>{icon}</Icon>
                  </IconButton>
                </div>);
              break;
            case DATA_TYPE_TEXT_BUTTON:
              valueFormatter = (val) => (
                <div style={{ textAlign: "center" }}>
                  <Button
                    variant="text"
                    size="small"
                    sx={{ 
                      textTransform: "none",
                      minWidth: "auto",
                      padding: 0,
                      "&:hover": {
                        backgroundColor: "transparent",
                      },
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (this.props.onCellButtonClick) {
                        // make the row stay highlighted
                        let originalIndex = this.state.dataSource.find(src => src.originalIndex === i)[ORIGINAL_INDEX];
                        this.setState({ singleRowSelectedIndex: originalIndex });
                        this.props.onCellButtonClick(i, col.key);
                      }
                    }}
                  >
                    <span>{val}</span>
                  </Button>
                </div>);
              break;
          }
        }

        if (valueFormatter) {
          acc[col.key] = valueFormatter(acc[col.key]);
        }

        return acc;
      }, { [ORIGINAL_INDEX]: i })); //original index is to get back to the item after sorting, or return the sort to a default of none

    let filterCols = this.state.columnDefinitions.filter(cd => cd.filterType === FILTER_TYPE_MULTI_SELECT || cd.filterType === FILTER_TYPE_MULTI_SELECT_NOLABEL);
    let filterValues = filterCols.reduce((acc, fc) => {
      // @ts-ignore
      acc[fc.key] = [...new Set(newDS.map(r => r[fc.key + (fc.sortValue ? SORT_VALUE : "")]))]
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b));
      return acc;
    }, {});

    newDS = filterRows(newDS, this.getFilters());

    newDS.sort(this.comparer);

    let pins = [];
    if (this.props.actions && this.props.actions.some(a => a === ACTION_MAP || (a as MGridAction).code === ACTION_MAP) && this.props.getMapPinForRow) {
      pins = newDS.map(r => {
        let pin = this.props.getMapPinForRow(r[ORIGINAL_INDEX]);
        return {
          id: r[ORIGINAL_INDEX],
          latitude: parseFloat(pin.latitude),
          longitude: parseFloat(pin.longitude),
          icon: { url: pin.iconURL, ...StandardPinSize }
        };
      });
    }

    let stateUpdate = { dataSource: newDS, pinsForMap: pins, dropdownFilterValues: filterValues, lastIndexSelected: null };

    if (sendNullFirst) {
      //sometimes the react-data-grid glitches out when you set its state out from under it. if you null the data source out first it then fixes that issue
      this.setState({ dataSource: null }, () => this.setState(stateUpdate));
    } else {
      this.setState(stateUpdate);
    }
  }

  rowGetter(index) {
    return this.state.dataSource[index];
  }

  handleFilterChange(filter) {
    //sadly when removing filters that were there when the page loaded, the silly react data grid doesnt always take a hint and show all of its content. so here is yet another resize hack :(
    for (let i = 0; i < 3; i++) setTimeout(() => window.dispatchEvent(new Event("resize")), i * 1000);
    let cc = this.columnConfig.find(cc => cc.key === filter.column.key);
    //empty filter, delete it
    if (filter.filterTerm === "" || filter.filterTerm.length === 0) {
      delete cc.filter;
    } else {
      if (Array.isArray(filter.filterTerm)) cc.filter = filter.filterTerm.map(f => f.value);
      else cc.filter = filter.filterTerm;
    }

    this.updateDataSource();
  }

  getValidFilterValues(columnId) {
    return this.state.dropdownFilterValues[columnId];
  }

  comparer(a, b) {
    let sortDirection = this.state.sortDirection;
    let sortType;

    let aValue, bValue;
    if (!sortDirection || sortDirection === "NONE") {
      //if sort dir is NONE we will sort the dataset how it was sent into us, by the original index asc
      sortDirection = "ASC";
      sortType = DATA_TYPE_NUMBER;

      aValue = a[ORIGINAL_INDEX];
      bValue = b[ORIGINAL_INDEX];
    } else {
      let sortColumn = this.state.sortColumn;
      let sortColumnDef = this.state.columnDefinitions.find(c => c.key === sortColumn);
      // sortColumnDef is null if a user hides the currently sorted column
      if (!sortColumnDef) {
        sortColumnDef = this.state.columnDefinitions[0];
        this.setState({
          sortColumn: null,
          sortDirection: "NONE"
        });
      }
      sortType = sortColumnDef.dataType;

      //grab the original value to sort by, not the formatted value
      aValue = a[sortColumn + SORT_VALUE] || a[sortColumn + RAW_ORIGINAL];
      bValue = b[sortColumn + SORT_VALUE] || b[sortColumn + RAW_ORIGINAL];
    }


    switch (sortType) {
      case DATA_TYPE_STRING:
        aValue = "" + aValue;
        bValue = "" + bValue;
        break;
      case DATA_TYPE_DATE:
      case DATA_TYPE_DATE_TIME:
      case DATA_TYPE_DATETIME_WITHDOW:
      case DATA_TYPE_DATE_WITHDOW:
      case DATA_TYPE_DAYS_ELAPSED:
        aValue = new Date(aValue);
        bValue = new Date(bValue);
        break;
      case DATA_TYPE_BUTTON:
        aValue = a[ORIGINAL_INDEX];
        bValue = b[ORIGINAL_INDEX];
        break;
      case DATA_TYPE_NUMBER:
      case DATA_TYPE_NUMBER_HUNDREDTHS:
      case DATA_TYPE_NUMBER_TENTHS:
      case DATA_TYPE_PERCENT:
      case DATA_TYPE_PERCENT_WHOLE_NUMBER:
      case DATA_TYPE_PERCENT_DECIMAL:
      case DATA_TYPE_NUMBER_UNFORMATTED:
      case DATA_TYPE_NUMBER_RAW:
      case DATA_TYPE_MONEY:
        //cant sort with NaN because it returns false always and messes up the order
        aValue = aValue && parseFloat(aValue);
        bValue = bValue && parseFloat(bValue);
        break;
      case DATA_TYPE_BOOLEAN:
        aValue = Boolean(aValue);
        bValue = Boolean(bValue);
        break;
    }

    if (sortType === DATA_TYPE_STRING) {
      let value = aValue.localeCompare(bValue);
      if (sortDirection === "ASC") return value;
      if (sortDirection === "DESC") return value * -1;
    } else {
      if (sortDirection === "ASC") {
        return (aValue > bValue) ? 1 : -1;
      } else if (sortDirection === "DESC") {
        return (aValue < bValue) ? 1 : -1;
      }
    }
  }

  onRowsSelected(rows) {
    //dont let shift select select text on the page
    if (window.getSelection) window.getSelection().removeAllRanges();
    // @ts-ignore not sure what browser this was to support so i wont remove it, but it definitely isnt a standard thing
    else if (document.selection) document.selection.empty();

    let isIndexSelected = index => this.getSelectedIndexes().indexOf(index) >= 0;

    if (!rows.length) return;

    let previ = this.state.lastIndexSelected;

    let rowRange = rows.map(r => r.rowIdx);
    let i = rowRange[0];
    let isSelect = true;//default to true in case multiple rows were passed in

    //multiple rows sent when select/deselect all is pressed
    //multiple rows also used to be sent for multi select, but that seemed to stop working with react upgrade and didnt work all the time anyways so i implemented it myself
    if (rows.length == 1) {
      if (this.state.shiftKeyDown && previ != null) {
        for (let j = Math.min(i, previ) + 1; j <= Math.max(i, previ); j++)rowRange.push(j);

        isSelect = isIndexSelected(this.state.lastIndexSelected);
      } else {
        isSelect = !isIndexSelected(i);
      }
    }

    const originalSelectedIndexes = rowRange.map(i => this.state.dataSource[i][ORIGINAL_INDEX]);
    let selectedIndexes;
    if (isSelect) {
      // @ts-ignore
      selectedIndexes = [...new Set(this.props.selectedRows.concat(originalSelectedIndexes))];
    } else {
      selectedIndexes = this.props.selectedRows.filter(j => !originalSelectedIndexes.includes(j));
    }

    this.setState({ lastIndexSelected: i });
    if (this.props.onRowSelectionChanged) this.props.onRowSelectionChanged(selectedIndexes);
  }

  onRowsDeselected(rows) {
    //multiple rows sent when select/deselect all is pressed
    //multiple rows also used to be sent for multi select, but that seemed to stop working with react upgrade and didnt work all the time anyways so i implemented it myself
    if (rows.length > 1) {
      const rowRange = rows.map(r => this.state.dataSource[r.rowIdx][ORIGINAL_INDEX]);
      const selectedIndexes = this.props.selectedRows.filter(j => !rowRange.includes(j));
      if (this.props.onRowSelectionChanged) this.props.onRowSelectionChanged(selectedIndexes);
      this.setState({ lastIndexSelected: null });
    } else {
      this.onRowsSelected(rows);//this will handle select and deselect
    }
  }

  getSelectedIndexes() {
    if (this.props.selectedRows) {
      return this.props.selectedRows.map(i => this.state.dataSource.findIndex(r => r[ORIGINAL_INDEX] === i)).filter(i => i >= 0);
    } else if (this.state.singleRowSelectedIndex !== null) {
      let index = this.state.dataSource.findIndex(r => r[ORIGINAL_INDEX] === this.state.singleRowSelectedIndex);
      if (index >= 0) return [index];
    } else {
      return [];
    }
  }

  // Returns a Set of selected row keys for react-data-grid v7
  getSelectedRowKeys(): Set<number> {
    const selectedRows = this.props.selectedRows || [];
    return new Set(selectedRows);
  }

  // Handle row selection changes from react-data-grid v7
  handleSelectedRowsChange = (selectedRowKeys: Set<number>) => {
    if (this.props.onRowSelectionChanged) {
      const selectedIndexes = Array.from(selectedRowKeys);
      this.props.onRowSelectionChanged(selectedIndexes);
    }
  };

  // Build columns array for react-data-grid v7
  getGridColumns(): Column<DataRow>[] {
    const columns: Column<DataRow>[] = [];
    
    // Add selection column if row selection is enabled
    if (this.props.allowRowSelection) {
      columns.push({
        ...SelectColumn,
        frozen: true,
      });
    }
    
    // Map column definitions to v7 format
    this.state.columnDefinitions.forEach(col => {
      // Check if this column is currently being sorted
      const isSorted = this.state.sortColumn === col.key;
      const sortDir = isSorted ? this.state.sortDirection : null;
      
      // Determine the appropriate header renderer
      const getHeaderRenderer = () => {
        if ((col as any).filterable && !this.props.disableFilters) {
          return (props: RenderHeaderCellProps<DataRow>) => (
            <div className="rdg-header-cell-content">
              <div className="rdg-header-cell-name" title={col.headerTooltip || col.name}>
                {col.name}
                {isSorted && sortDir && sortDir !== 'NONE' && (
                  <span className="rdg-sort-icon">{sortDir === 'ASC' ? ' ▲' : ' ▼'}</span>
                )}
              </div>
              {this.state.showFilterBar && col.filterRenderer && (
                <div 
                  className="rdg-header-filter" 
                  onClick={(e) => e.stopPropagation()} 
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  {typeof col.filterRenderer === 'function' 
                    // @ts-ignore - filterRenderer expects different props format
                    ? col.filterRenderer({ 
                        column: { key: col.key, name: col.name },
                        onChange: this.handleFilterChange,
                        getValidFilterValues: (columnKey) => this.getValidFilterValues(columnKey)
                      })
                    : col.filterRenderer}
                </div>
              )}
            </div>
          );
        } else if (col.headerRenderer) {
          return () => (
            <span>
              {col.headerRenderer}
              {isSorted && sortDir && sortDir !== 'NONE' && (
                <span className="rdg-sort-icon">{sortDir === 'ASC' ? ' ▲' : ' ▼'}</span>
              )}
            </span>
          );
        } else {
          return () => (
            <span title={col.headerTooltip || col.name}>
              {col.name}
              {isSorted && sortDir && sortDir !== 'NONE' && (
                <span className="rdg-sort-icon">{sortDir === 'ASC' ? ' ▲' : ' ▼'}</span>
              )}
            </span>
          );
        }
      };

      // Determine the appropriate cell renderer
      const getCellRenderer = () => {
        if (col.formatter) {
          return ({ row, column: colDef }: RenderCellProps<DataRow>) => {
            const value = row[colDef.key];
            return col.formatter({ value, row });
          };
        }
        return undefined;
      };

      const column: Column<DataRow> = {
        key: col.key,
        name: col.name,
        width: col.width,
        minWidth: col.width ? Math.min(col.width, 50) : 50,
        resizable: col.resizable !== false,
        sortable: col.sortable !== false,
        draggable: this.props.draggableColumns !== false,
        renderCell: getCellRenderer(),
        renderHeaderCell: getHeaderRenderer(),
      };

      columns.push(column);
    });

    return columns;
  }

  // Custom sort status renderer for v7
  renderSortStatus = ({ sortDirection, priority }: { sortDirection: string | undefined, priority: number | undefined }) => {
    if (!sortDirection) return null;
    return (
      <span className="rdg-sort-icon">
        {sortDirection === 'ASC' ? '▲' : '▼'}
      </span>
    );
  };

  onGridSort(sortColumn, sortDirection) {
    this.setState({ sortColumn: sortColumn, sortDirection: sortDirection, lastIndexSelected: null }, this.sortData);
  }

  sortData() {
    this.setState({ dataSource: this.state.dataSource.slice(0).sort(this.comparer) });
  }

  onRowClick(i) {
    if (i < 0) return; //yea this gets called when we click on the header...
    let originalIndex = this.state.dataSource[i][ORIGINAL_INDEX];
    this.setState({ singleRowSelectedIndex: originalIndex });
    if (this.props.onRowClick) {
      //if a row is double clicked and we push to another page the next page loads fully selected. So this code will clear the document selection after half a second to prevent that
      setTimeout(() => document.getSelection().removeAllRanges(), 500);
      this.props.onRowClick(originalIndex);
    }
  }

  onMapClick(pin) {
    if (this.props.onRowClick) this.props.onRowClick(pin.id);
  }

  onHeaderDrop(source, target) {
    const newDefs = this.state.columnDefinitions.slice(0);
    const columnSourceIndex = this.state.columnDefinitions.findIndex(i => i.key === source);
    const columnTargetIndex = this.state.columnDefinitions.findIndex(i => i.key === target);

    newDefs.splice(columnTargetIndex, 0, newDefs.splice(columnSourceIndex, 1)[0]);

    //update the column config
    newDefs.forEach((cd, i) => this.columnConfig.find(nc => nc.key === cd.key).order = i);
    this.saveColumnConfig();

    //for some reason you first need to clear the columns in the state to change them, i imagine its because the grid only knows its data changed once the count of columns changes
    this.setState({ columnDefinitions: null }, () => this.setState({ columnDefinitions: newDefs }));
  }

  toggleColumnVisible(col) {
    let columnConfigForDialog = this.state.columnConfigForDialog.slice(0);
    columnConfigForDialog.find(c => c.key === col.key).visible = !col.visible;
    this.setState({ columnConfigForDialog });
  }

  getNumItemsFoundText() {
    let totalCount = this.props.data.length;
    let items = `Item${totalCount !== 1 ? "s" : ""}`;
    let filterCount = this.state.dataSource ? this.state.dataSource.length : totalCount;
    let showing = filterCount < totalCount ? `Showing ${commaFormat(filterCount)} of ` : "";

    return (
      <span className="grid-header-results-string">
        &nbsp;
        {`${showing}${commaFormat(totalCount)} ${items} Found`}
        {showing && <a style={{ marginLeft: 25 }} tabIndex={0} onClick={this.onClearFilters}><i>Clear Filters</i></a>}
      </span>
    );
  }

  gridWrapperCallbackRef(element) {
    if (element) {
      //when the react data grid gets resized we will dispatch a window resize event
      //react data grid only listens for window resizes, even though theres other ways it could get resized
      //this will resolve that issue
      new ResizeObserver(this.rateLimitedResize).observe(element);
    }
  }

  render() {
    if (this.props.isFetchingData ||
      this.props.data == null ||
      !this.state.columnDefinitions ||
      !this.state.dataSource ||
      (this.props.configurationId && (!this.props.preferences[this.preferenceName()] || this.props.preferences[this.preferenceName()].isFetching))) {
      return <div className="mgrid-spinner"><MSpinner /></div>;
    } else {

      // this is actually broken and could just be removed (along with its reference below "rowRenderer={RowRenderer}") but shannon was worried about that going through test and i didnt feel like fighting that battle
      // what happens is if you click a row 5 and then sort, the proper row will be highlighted along with the wrong row. row index 5 will still be highlighted and the row that originally was index 5 but now isnt because of the sort will also be highlighted
      let RowRenderer = ({ renderBaseRow, ...props }) => {
        const clicked = this.state.singleRowSelectedIndex == props.idx && !props.isSelected;
        return renderBaseRow({ ...props, extraClasses: clicked ? "clicked" : "" });
      };

      let gridClassName = "mgrid-react-data-grid";
      if (this.props.className) gridClassName += ` ${this.props.className}`;
      if (this.props.cardStyle) gridClassName += " card-style";

      const gridStyle: React.CSSProperties = this.props.wrapperStyle || {};

      return (
          <div className={gridClassName} ref={this.gridWrapperCallbackRef} {...(Object.keys(gridStyle).length ? { style: gridStyle } : {})}>
          {(this.props.headerText || this.props.actions) &&
            <div className="mgrid-header">
              <div className="mgrid-filters" />
              <div className="mgrid-header-text">
                <div className="mgrid-title">
                  {this.props.headerText || ""}
                </div>
                { this.props.displayNumOfItemsFound && this.getNumItemsFoundText() }
              </div>

              {this.props.actions && !!this.props.actions.length && (
                <span className="mgrid-actions">{this.getGridActions()}
                </span>
              )}
            </div>}
          {this.state.viewMode == VIEW_MODE_GRID ? (
              <ReactDataGrid
                columns={this.getGridColumns()}
                rows={this.state.dataSource || []}
                selectedRows={this.props.allowRowSelection ? this.getSelectedRowKeys() : undefined}
                onSelectedRowsChange={this.props.allowRowSelection ? this.handleSelectedRowsChange : undefined}
                onCellClick={(args, event) => {
                  // Don't trigger row click for selection column
                  if (args.column.key !== SelectColumn.key) {
                    this.onRowClick(args.rowIdx);
                  }
                }}
                onSortColumnsChange={(sortColumns) => {
                  if (sortColumns.length > 0) {
                    const { columnKey, direction } = sortColumns[0];
                    this.onGridSort(columnKey, direction);
                  } else {
                    this.onGridSort(null, 'NONE');
                  }
                }}
                sortColumns={this.state.sortColumn && this.state.sortDirection !== 'NONE' 
                  ? [{ columnKey: this.state.sortColumn, direction: this.state.sortDirection as 'ASC' | 'DESC' }] 
                  : []}
                onColumnsReorder={(sourceKey, targetKey) => {
                  this.onHeaderDrop(sourceKey, targetKey);
                }}
                rowKeyGetter={(row) => row[ORIGINAL_INDEX]}
                defaultColumnOptions={{
                  resizable: true,
                  sortable: true,
                }}
                className="rdg-light fill-grid"
                style={{ height: '100%' }}
                rowClass={(row) => {
                  const rowIdx = this.state.dataSource.findIndex(r => r[ORIGINAL_INDEX] === row[ORIGINAL_INDEX]);
                  const classes: string[] = [];
                  
                  // Row clicked highlighting (when selection is disabled)
                  if (!this.props.allowRowSelection && this.state.singleRowSelectedIndex === row[ORIGINAL_INDEX]) {
                    classes.push('row-clicked');
                  }
                  
                  // Row selection highlighting  
                  if (this.props.allowRowSelection && this.props.selectedRows?.includes(row[ORIGINAL_INDEX])) {
                    classes.push('row-selected');
                  }
                  
                  return classes.length > 0 ? classes.join(' ') : undefined;
                }}
                headerRowHeight={this.state.showFilterBar ? 70 : 35}
              />
          ) : (
              <Map
                containerClass="map-control"
                loadingClass="map-control"
                mapClass="map-control"
                onClick={this.onMapClick}
                onMouseOver={this.onMouseOver}
                onMouseOut={this.onMouseOut}
                pins={this.state.pinsForMap}
              />
            )}
          {this.state.columnConfigForDialog && <Dialog
            id="configure-columns"
            className="card-dialog"
            open={true}
            onClose={this.onConfigureColumnsCancelled}
            sx={{ '& .MuiDialog-paper': { width: 350 } }}>
            <DialogTitle>
              <div className="card-dialog-title">
                {SELECT_COLUMNS}
                <i className="md-icon material-icons dialog-button" title="Restore Default View" onClick={this.onConfigureColumnsReset}>replay</i>
                <i className="md-icon material-icons dialog-button" title="Apply" onClick={this.onConfigureColumnsApplied}>done</i>
                <i className="md-icon material-icons dialog-button" title="Close" onClick={this.onConfigureColumnsCancelled}>close</i>
              </div>
            </DialogTitle>
            <DialogContent>
              <TableContainer component="div" className="card-dialog-list">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell className="dialog-select-list-checked">Show</TableCell>
                      <TableCell className="dialog-select-list-label">Column Name</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {this.state.columnConfigForDialog.map(c => (
                      <TableRow onClick={(e) => { e.preventDefault(); this.toggleColumnVisible(c); }} key={c.key}>
                        <TableCell style={{ verticalAlign: "top", paddingTop: "5px" }} >
                          {/* need to provide name, id, label, and onChange so the checkbox list doesnt throw any warnings, onChange is used instead of changing checked to defaultChecked because then the box will not change its checked value when it is clicked*/}
                          <Checkbox checked={c.visible} onChange={() => { }} />
                        </TableCell>
                        <TableCell className="dialog-select-list-label" style={{ verticalAlign: "center" }}>{c.name}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </DialogContent>
          </Dialog>}
          {this.state.mouseOverControl && <MToolTip {...this.state.mouseOverControl} />}
        </div>);
    }
  }
}

const ORIGINAL_INDEX = "originalIndex";//property added to each row so we can return sorting to default and also find the original row when onclick is fired
const RAW_ORIGINAL = "RawOriginal";//each value is saved with its raw value also, appending this string to each column key to make another property
const TIP_TEXT = "FormatterTipText";//used to store custom tip text generated for each row
const SORT_VALUE = "SortValue";//used to store the value that we use to sort on if its not the same as the value displayed
const EXPORT_VALUE = "ExcelExportValue";//if provided this will override the value that gets exported to excel on download
const PREFERENCE_PREFIX = "GridConfiguration-";
const SELECT_COLUMNS = "Display Columns Temporarily";
const VIEW_MODE_GRID = "VIEW_MODE_GRID";
const VIEW_MODE_MAP = "VIEW_MODE_MAP";

export const FILTER_TYPE_NONE = "FILTER_TYPE_NONE";
export const FILTER_TYPE_TEXT = "FILTER_TYPE_TEXT";
export const FILTER_TYPE_MULTI_SELECT = "FILTER_TYPE_MULTI_SELECT";
export const FILTER_TYPE_MULTI_SELECT_NOLABEL = "FILTER_TYPE_MULTI_SELECT_NOLABEL";

export const COL_CODE_CONTROL_NUMBER = "COL_CODE_CONTROL_NUMBER";
export const COL_CODE_SURVEY_TYPE = "COL_CODE_SURVEY_TYPE";
export const COL_CODE_FULL_NAME = "COL_CODE_FULL_NAME";
export const COL_CODE_INSURED_NAME = "COL_CODE_INSURED_NAME";
export const COL_CODE_FR_NUMBER = "COL_CODE_FR_NUMBER";
export const COL_CODE_PAYROLL_EMPLOYEE_ID = "COL_CODE_PAYROLL_EMPLOYEE_ID";
export const COL_CODE_ADDRESS = "COL_CODE_ADDRESS";
export const COL_CODE_CITY = "COL_CODE_CITY";
export const COL_CODE_STATE = "COL_CODE_STATE";
export const COL_CODE_AGENT_STATE = "COL_CODE_AGENT_STATE";
export const COL_CODE_ZIP = "COL_CODE_ZIP";
export const COL_CODE_COUNTY = "COL_CODE_COUNTY";
export const COL_CODE_EMAIL = "COL_CODE_EMAIL";
export const COL_CODE_IS_ACTIVE = "COL_CODE_IS_ACTIVE";
export const COL_CODE_LATITUDE = "COL_CODE_LATITUDE";
export const COL_CODE_LONGITUDE = "COL_CODE_LONGITUDE";
export const COL_CODE_CUSTOMER_NAME = "COL_CODE_CUSTOMER_NAME";
export const COL_CODE_CUSTOMER_NUMBER = "COL_CODE_CUSTOMER_NUMBER";
export const COL_CODE_MAC = "COL_CODE_MAC";
export const COL_CODE_POLICY_NUMBER = "COL_CODE_POLICY_NUMBER";
export const COL_CODE_DATE = "COL_CODE_DATE";

export const ACTION_MAP = "ACTION_MAP";
export const ACTION_DOWNLOAD = "ACTION_DOWNLOAD";
export const ACTION_REFRESH = "ACTION_REFRESH";
export const ACTION_CONFIGURE = "ACTION_CONFIGURE";
export const ACTION_ADD = "ACTION_ADD";
export const ACTION_DELETE = "ACTION_DELETE";
export const ACTION_EDIT = "ACTION_EDIT";
export const ACTION_CANCEL = "ACTION_CANCEL";
export const ACTION_SAVE = "ACTION_SAVE";

export const FORMATTER_RIGHT_ALIGN = ({ value }) => {
  return <div style={{ textAlign: "right" }}>{value}</div>;
};

FORMATTER_RIGHT_ALIGN.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export const FORMATTER_LEFT_ALIGN = ({ value }) => {
  return <div style={{ textAlign: "left" }}>{value}</div>;
};

FORMATTER_LEFT_ALIGN.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export const FORMATTER_CENTER_ALIGN = ({ value }) => {
  return <div style={{ textAlign: "center" }}>{value}</div>;
};

FORMATTER_CENTER_ALIGN.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

/** Has a 1em left margin to indent the value */
export const FORMATTER_INDENTED = ({ value }) => {
  return <div style={{ marginLeft: "1em" }}>{value}</div>;
};
FORMATTER_INDENTED.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

const  filterValues = function (row: DataRow, columnFilter, columnKey) {
  //if theres a sort value on the row use that for comparison
  let rowValue = row[columnKey + SORT_VALUE] || row[columnKey];
  if (columnFilter === null) {
    return false;
  } else if (Array.isArray(columnFilter.filterTerm)) {
    //if nothing is selected we count it as everything being selected, not sure why, but this is how the old grid works so its how i made this work
    //it may have something to do with wanting to default the dropdown to having nothing selected but still wanting to see everyhing,
    //so when you click to see things you are selecting what you want to see, because is everything was selected in the dropdown by default then
    //when you click things you would be deselecting them instead and that was probably not the desired function
    if (columnFilter.filterTerm.length === 0) return true;
    return columnFilter.filterTerm.some(f => rowValue == f.value);
  } else {
    return rowValue == columnFilter.filterTerm.value;
  }
};

const colConfigSort = (a, b) => {
  let sortALast = !a.visible;
  let sortBLast = !b.visible;
  if (!sortALast && sortBLast) return -1;
  if (sortALast && !sortBLast) return 1;
  if (!sortALast && !sortBLast) return a.order > b.order ? 1 : -1;
  return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
};

interface ToolbarProps {
  onToggleFilter?: () => void,
}

class Toolbar extends Component<ToolbarProps> {
  hasToggledFilter: boolean;

  constructor(props: ToolbarProps) {
    super(props);
    this.hasToggledFilter = false;
  }

  componentDidUpdate() {
    //this is the best lifecycle method i have found to call this toggle filter method.
    //if its called on mount it doesnt work

    //note from later: i dont think this code even works? document.toggle doesnt seem to be a thing
    //it looks like ToolbarProps.onToggleFilter isnt even used anymore
    // @ts-ignore
    document.toggle = this.props.onToggleFilter;
    if (!this.hasToggledFilter) {
      this.props.onToggleFilter();
      this.hasToggledFilter = true;
    }
  }

  render() {
    return (null);
  }
}

const columnDefaults = {
  visible: true,
  resizable: false,
  sortable: true,
  dataType: DATA_TYPE_STRING,
  filterType: FILTER_TYPE_TEXT,
};

const getGridColumnDefaultsFromCode = (code) => {
  switch (code) {
    case COL_CODE_CONTROL_NUMBER:
      return {
        ...columnDefaults,
        key: "controlnum",
        name: "Control #",
        width: 90,
        dataType: DATA_TYPE_NUMBER_RAW,
        formatter: FORMATTER_RIGHT_ALIGN
      };
    case COL_CODE_FULL_NAME:
      return {
        ...columnDefaults,
        key: "name",
        name: "Name",
        width: 160
      };
    case COL_CODE_INSURED_NAME:
      return {
        ...columnDefaults,
        key: "insuredFullName",
        name: "Insured",
        width: 140
      };
    case COL_CODE_FR_NUMBER:
      return {
        ...columnDefaults,
        key: "frNumber",
        name: "Field Rep #",
        width: 110,
        dataType: DATA_TYPE_NUMBER_RAW
      };
    case COL_CODE_PAYROLL_EMPLOYEE_ID:
      return {
        ...columnDefaults,
        key: "payrollEmployeeId",
        name: "Payroll ID",
        width: 110,
        dataType: DATA_TYPE_STRING
      };
    case COL_CODE_ADDRESS:
      return {
        ...columnDefaults,
        key: "address",
        name: "Address",
        width: 170
      };
    case COL_CODE_CITY:
      return {
        ...columnDefaults,
        key: "city",
        name: "City",
        width: 120
      };
    case COL_CODE_STATE:
      return {
        ...columnDefaults,
        key: "state",
        name: "State",
        width: 57,
        filterType: FILTER_TYPE_MULTI_SELECT,
        formatter: FORMATTER_CENTER_ALIGN
      };
    case COL_CODE_AGENT_STATE:
      return {
        ...columnDefaults,
        key: "state",
        name: "State",
        width: 120,
        filterType: FILTER_TYPE_MULTI_SELECT,
        formatter: FORMATTER_CENTER_ALIGN
      };
    case COL_CODE_ZIP:
      return {
        ...columnDefaults,
        key: "zip",
        name: "Zip",
        width: 55,
        dataType: DATA_TYPE_NUMBER_RAW
      };
    case COL_CODE_COUNTY:
      return {
        ...columnDefaults,
        key: "county",
        name: "County",
        width: 120
      };
    case COL_CODE_EMAIL:
      return {
        ...columnDefaults,
        key: "email",
        name: "Email",
        width: 300
      };
    case COL_CODE_IS_ACTIVE:
      return {
        ...columnDefaults,
        key: "isActive",
        name: "Active",
        width: 65,
        dataType: DATA_TYPE_BOOLEAN
      };
    case COL_CODE_LATITUDE:
      return {
        ...columnDefaults,
        key: "latitude",
        name: "Latitude",
        width: 100,
        dataType: DATA_TYPE_NUMBER_RAW
      };
    case COL_CODE_LONGITUDE:
      return {
        ...columnDefaults,
        key: "longitude",
        name: "Longitude",
        width: 100,
        dataType: DATA_TYPE_NUMBER_RAW
      };
    case COL_CODE_SURVEY_TYPE:
      return {
        ...columnDefaults,
        key: "surveyType",
        name: "Survey Type",
        width: 200
      };
    case COL_CODE_CUSTOMER_NAME:
      return {
        ...columnDefaults,
        key: "customer",
        name: "Customer",
        width: 260
      };
    case COL_CODE_CUSTOMER_NUMBER:
      return {
        ...columnDefaults,
        key: "customerNumber",
        name: "Customer #",
        width: 120
      };
    case COL_CODE_MAC:
      return {
        ...columnDefaults,
        key: "mac",
        name: "Mac",
        width: 60,
        dataType: DATA_TYPE_MONEY
      };
    case COL_CODE_POLICY_NUMBER:
      return {
        ...columnDefaults,
        key: "policyNumber",
        name: "Policy Number",
        width: 120
      };
    case COL_CODE_DATE:
      return {
        ...columnDefaults,
        key: "date",
        width: 120,
        dataType: DATA_TYPE_DATETIME_WITHDOW,
      };
    default:
      return columnDefaults;
  }
};

const mapStateToProps = (state) => {
  return {
    preferences: state.preferences
  };
};

const mapDispatchToProps = (dispatch) => ({
  savePreference(code, value) {
    dispatch(savePreference(code, value));
  },
  loadPreference(code) {
    dispatch(loadPreference(code));
  }
});

// export the proper type so we get some autocomplete elsewhere
export default connect(mapStateToProps, mapDispatchToProps)(MGrid) as typeof MGrid;
